import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TambolaBall } from '../components/TambolaBall';
import { useAuthStore } from '../store/auth.store.js';

const LOADING_STEPS = [
  { progress: 0, text: 'Preparing Experience...' },
  { progress: 25, text: 'Loading Audio & Game Assets...' },
  { progress: 55, text: 'Connecting to Game Server...' },
  { progress: 85, text: 'Synchronizing Lobby State...' },
  { progress: 100, text: 'Ready!' },
];

export function SplashScreen() {
  const navigate = useNavigate();
  const { checkSession } = useAuthStore();
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('Preparing Experience...');

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Simulate loading timer
  useEffect(() => {
    const totalDuration = 3500; // 3.5 seconds
    const intervalTime = 50;
    const increment = (100 / totalDuration) * intervalTime;

    const timer = setInterval(() => {
      setProgress((prev) => {
        const next = Math.min(prev + increment, 100);
        
        // Update loading status messages based on current progress
        const currentStep = [...LOADING_STEPS]
          .reverse()
          .find((step) => next >= step.progress);
        
        if (currentStep) {
          setStatusText(currentStep.text);
        }

        if (next >= 100) {
          clearInterval(timer);
          // Auto redirect after a short delay
          setTimeout(() => {
            if (useAuthStore.getState().user) {
              navigate('/home');
            } else {
              navigate('/login');
            }
          }, 400);
        }
        return next;
      });
    }, intervalTime);

    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div className="relative flex h-screen w-screen items-center justify-center overflow-hidden bg-mesh bg-cover select-none">
      
      {/* Dynamic light streak animation overlay */}
      <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/10 via-transparent to-blue-900/10 mix-blend-color-dodge opacity-50 pointer-events-none" />

      {/* Floating background glowing ambient blobs */}
      <div className="absolute -top-12 -left-12 w-96 h-96 rounded-full bg-blue-500/10 blur-[100px] animate-pulse" />
      <div className="absolute -bottom-16 -right-16 w-[450px] h-[450px] rounded-full bg-purple-500/10 blur-[120px] animate-pulse" />

      {/* Floating Translucent Background Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            x: [0, 40, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-1/4 left-1/5 w-16 h-16 rounded-full bg-white/5 backdrop-blur-[2px] border border-white/10"
        />
        <motion.div
          animate={{
            x: [0, -30, 0],
            y: [0, 50, 0],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-1/3 right-1/4 w-24 h-24 rounded-full bg-white/3 backdrop-blur-[3px] border border-white/5"
        />
        <motion.div
          animate={{
            x: [0, 20, 0],
            y: [0, 20, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-2/3 left-1/3 w-8 h-8 rounded-full bg-white/5 backdrop-blur-[1px] border border-white/15"
        />
      </div>

      {/* Floating Decorative 3D Tambola Balls */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
        {/* Top Left Area */}
        <TambolaBall
          number={7}
          color="gold"
          size={56}
          className="absolute top-[12%] left-[10%]"
          delay={0.2}
          duration={3.5}
          yOffsetRange={12}
        />
        <TambolaBall
          number={24}
          color="purple"
          size={44}
          className="absolute top-[28%] left-[6%]"
          delay={0.5}
          duration={4.2}
          yOffsetRange={18}
        />

        {/* Bottom Left Area */}
        <TambolaBall
          number={90}
          color="magenta"
          size={64}
          className="absolute bottom-[15%] left-[12%]"
          delay={0.4}
          duration={3.8}
          yOffsetRange={14}
        />

        {/* Top Right Area */}
        <TambolaBall
          number={45}
          color="blue"
          size={60}
          className="absolute top-[15%] right-[12%]"
          delay={0.3}
          duration={4.0}
          yOffsetRange={16}
        />
        <TambolaBall
          number={12}
          color="gold"
          size={40}
          className="absolute top-[32%] right-[7%]"
          delay={0.6}
          duration={3.6}
          yOffsetRange={10}
        />

        {/* Bottom Right Area */}
        <TambolaBall
          number={88}
          color="purple"
          size={68}
          className="absolute bottom-[18%] right-[10%]"
          delay={0.1}
          duration={4.5}
          yOffsetRange={20}
        />
      </div>

      {/* Center Brand Component */}
      <div className="relative flex flex-col items-center justify-center text-center z-20">
        
        {/* Glowing aura under logo */}
        <div className="absolute w-72 h-72 rounded-full bg-purple-600/20 blur-3xl -z-10 animate-glow-pulse" />

        {/* Logo Text container with scaling entry */}
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="mb-3"
        >
          {/* Main Title: Gradual glowing color text */}
          <h1 
            style={{
              textShadow: '0 4px 10px rgba(0,0,0,0.6), 0 0 30px rgba(124,58,237,0.3)',
            }}
            className="font-display text-5xl md:text-7xl font-extrabold tracking-wider bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
          >
            TAMBOLA
          </h1>
          <h2 
            style={{
              textShadow: '0 4px 10px rgba(0,0,0,0.6), 0 0 40px rgba(240,101,149,0.3)',
            }}
            className="font-display text-4xl md:text-6xl font-black tracking-widest text-gradient"
          >
            ONLINE
          </h2>
        </motion.div>

        {/* Subtitle with fade-in delay */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 0.8, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="font-display text-sm md:text-base tracking-widest text-amber-200/90 font-medium uppercase"
        >
          Ultimate Multiplayer Housie
        </motion.p>
      </div>

      {/* Loading Progress Bar Container */}
      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 w-80 md:w-96 flex flex-col items-center justify-center z-20">
        
        {/* Progress label text */}
        <div className="h-6 overflow-hidden mb-2 text-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={statusText}
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 0.7 }}
              exit={{ y: -12, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="text-xs md:text-sm tracking-wider font-medium text-surface-200 uppercase"
            >
              {statusText}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Progress bar outer slot */}
        <div className="relative w-full h-[6px] rounded-full bg-slate-950/60 overflow-hidden border border-white/5 shadow-inner">
          
          {/* Active progress color stripe */}
          <motion.div
            style={{ width: `${progress}%` }}
            className="h-full rounded-full bg-gradient-to-r from-purple-600 via-accent-500 to-blue-500 shadow-[0_0_10px_rgba(124,58,237,0.8)]"
          />
        </div>
      </div>

      {/* Footer Branding */}
      <div className="absolute bottom-6 left-6 z-20 pointer-events-none opacity-50">
        <p className="text-[10px] md:text-xs tracking-widest font-mono uppercase text-surface-400">
          &copy; Tambola Online
        </p>
      </div>
      <div className="absolute bottom-6 right-6 z-20 pointer-events-none opacity-50">
        <p className="text-[10px] md:text-xs tracking-widest font-mono uppercase text-surface-400">
          Version v1.0.0
        </p>
      </div>

    </div>
  );
}
